/* ============================================================
   MARVIQ — assets/theme.js
   Global, cross-page behavior. Cart logic lives in cart.js,
   product-page logic lives in product.js, wishlist in
   marviq-wishlist.js — this file is everything else.
   ============================================================ */
(function () {
  "use strict";

  var routes = window.MARVIQ ? window.MARVIQ.routes : {};

  /* ---------- Splash loader ----------
     Hidden on EVERY page. Previously this lived only in hero.liquid
     (homepage), so any other template (/collections, /products, /cart)
     stayed stuck behind the full-screen loader and looked blank. */
  var splashLoader = document.getElementById("loader");
  if (splashLoader) {
    var hideLoader = function () {
      window.setTimeout(function () { splashLoader.classList.add("done"); }, 500);
    };
    if (document.readyState !== "loading") hideLoader();
    else document.addEventListener("DOMContentLoaded", hideLoader, { once: true });
    window.addEventListener("load", hideLoader, { once: true });
    window.setTimeout(function () { splashLoader.classList.add("done"); }, 4500);
  }

  /* ---------- Header scroll state ---------- */
  var header = document.getElementById("siteHeader");
  var totop = document.getElementById("totop");
  if (header) {
    window.addEventListener("scroll", function () {
      var y = window.scrollY;
      header.classList.toggle("scrolled", y > 10);
      if (totop) totop.classList.toggle("show", y > 700);
    }, { passive: true });
  }
  if (totop) totop.addEventListener("click", function () { window.scrollTo({ top: 0, behavior: "smooth" }); });

  /* ---------- Mobile drawer ---------- */
  var drawer = document.getElementById("drawer");
  var drawerOverlay = document.getElementById("drawerOverlay");
  var hamburger = document.getElementById("hamburger");
  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add("open");
    if (drawerOverlay) drawerOverlay.classList.add("open");
    if (hamburger) hamburger.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove("open");
    if (drawerOverlay) drawerOverlay.classList.remove("open");
    if (hamburger) hamburger.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
  }
  if (hamburger) hamburger.addEventListener("click", openDrawer);
  var drawerClose = document.getElementById("drawerClose");
  if (drawerClose) drawerClose.addEventListener("click", closeDrawer);
  if (drawerOverlay) drawerOverlay.addEventListener("click", closeDrawer);
  if (drawer) {
    drawer.querySelectorAll("a").forEach(function (a) { a.addEventListener("click", closeDrawer); });
    drawer.addEventListener("click", function (e) {
      var toggle = e.target.closest("[data-drawer-toggle]");
      if (toggle) toggle.closest(".drawer-cat").classList.toggle("open");
    });
  }

  /* ---------- Search modal + predictive search ---------- */
  var searchOverlay = document.getElementById("searchOverlay");
  var searchOpenBtn = document.getElementById("searchOpen");
  var searchInput = document.getElementById("headerSearchInput");
  var searchResultsPreview = document.getElementById("searchResultsPreview");
  if (searchOpenBtn && searchOverlay) {
    searchOpenBtn.addEventListener("click", function () {
      searchOverlay.classList.add("open");
      setTimeout(function () { if (searchInput) searchInput.focus(); }, 300);
    });
    searchOverlay.addEventListener("click", function (e) {
      if (e.target === searchOverlay) searchOverlay.classList.remove("open");
    });
  }
  if (searchInput && searchResultsPreview) {
    var debounceTimer;
    searchInput.addEventListener("input", function () {
      clearTimeout(debounceTimer);
      var q = searchInput.value.trim();
      if (q.length < 2) { searchResultsPreview.innerHTML = ""; return; }
      debounceTimer = setTimeout(function () {
        fetch("/search/suggest.json?q=" + encodeURIComponent(q) + "&resources[type]=product&resources[limit]=5&resources[options][fields]=title,product_type,tag")
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            if (!products.length) { searchResultsPreview.innerHTML = ""; return; }
            searchResultsPreview.innerHTML = products.map(function (p) {
              var img = p.featured_image ? p.featured_image.url : "";
              return (
                '<a class="search-result-row" href="' + p.url + '">' +
                (img ? '<img src="' + img + '" alt="" loading="lazy">' : "") +
                '<span><span class="srr-name">' + p.title + '</span><br><span class="srr-price">' + p.price + '</span></span>' +
                "</a>"
              );
            }).join("");
          })
          .catch(function () {});
      }, 220);
    });
  }

  /* ---------- Quick view ---------- */
  var qvOverlay = document.getElementById("qvOverlay");
  var qvCard = document.getElementById("qvCard");
  var qvClose = document.getElementById("qvClose");

  function qvTemplate(product) {
    var variant = product.variants[0];
    product.variants.forEach(function (v) { if (v.available) variant = v; });
    var priceHtml = "₹" + Number(variant.price).toLocaleString("en-IN");
    if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
      priceHtml += ' <span class="old">₹' + Number(variant.compare_at_price).toLocaleString("en-IN") + "</span>";
    }
    var sizeOption = product.options.indexOf("Size");
    var sizesHtml = "";
    if (sizeOption !== -1) {
      var seen = {};
      sizesHtml = '<div style="display:flex;gap:10px;margin:6px 0;flex-wrap:wrap;">' +
        product.variants.filter(function (v) {
          var val = v.options[sizeOption];
          if (seen[val]) return false;
          seen[val] = true;
          return true;
        }).map(function (v) {
          return '<button type="button" class="size-btn" style="min-width:auto;" data-qv-variant="' + v.id + '">' + v.options[sizeOption] + "</button>";
        }).join("") + "</div>";
    }
    return (
      '<button class="qv-close" id="qvClose" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" fill="none" width="16" height="16"><path d="M5 5L19 19M19 5L5 19" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>' +
      "</button>" +
      '<div class="qv-img"><img src="' + (product.featured_image || "") + '" alt="' + product.title + '"></div>' +
      '<div class="qv-info">' +
        '<span class="eyebrow">' + (product.type || product.vendor || "") + "</span>" +
        '<h3 class="h-lg" style="font-size:24px;">' + product.title + "</h3>" +
        '<div class="card-price" style="font-size:20px;">' + priceHtml + "</div>" +
        '<p style="font-size:13.5px;color:var(--muted);line-height:1.6;">' + (product.description || "").replace(/<[^>]*>/g, "").slice(0, 200) + "</p>" +
        sizesHtml +
        '<button class="btn btn-dark" id="qvAdd" data-variant-id="' + variant.id + '" style="justify-content:center;margin-top:6px;" ' + (variant.available ? "" : "disabled") + ">" +
          (variant.available ? "Add to Cart" : "Sold Out") +
        "</button>" +
        '<a class="btn btn-outline" href="' + product.url + '" style="justify-content:center;">View Full Details</a>' +
      "</div>"
    );
  }

  function openQuickView(handle) {
    if (!qvOverlay || !qvCard) return;
    qvOverlay.classList.add("open");
    qvCard.innerHTML =
      '<button class="qv-close" id="qvClose" aria-label="Close"><svg viewBox="0 0 24 24" fill="none" width="16" height="16"><path d="M5 5L19 19M19 5L5 19" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>' +
      '<div class="qv-loading">Loading…</div>';
    fetch("/products/" + handle + ".js")
      .then(function (r) { return r.json(); })
      .then(function (product) { qvCard.innerHTML = qvTemplate(product); })
      .catch(function () { qvOverlay.classList.remove("open"); });
  }

  document.addEventListener("click", function (e) {
    var qvTrigger = e.target.closest("[data-qv]");
    if (qvTrigger) { e.preventDefault(); openQuickView(qvTrigger.getAttribute("data-qv")); return; }

    if (e.target.closest("#qvClose") || e.target === qvOverlay) {
      if (qvOverlay) qvOverlay.classList.remove("open");
      return;
    }

    var qvAddBtn = e.target.closest("#qvAdd");
    if (qvAddBtn && !qvAddBtn.disabled && window.MarviqCart) {
      window.MarviqCart.add(qvAddBtn.getAttribute("data-variant-id"), 1).then(function () {
        if (qvOverlay) qvOverlay.classList.remove("open");
      });
      return;
    }
    var qvSizeBtn = e.target.closest("[data-qv-variant]");
    if (qvSizeBtn) {
      var addBtn = document.getElementById("qvAdd");
      if (addBtn) addBtn.setAttribute("data-variant-id", qvSizeBtn.getAttribute("data-qv-variant"));
      qvCard.querySelectorAll("[data-qv-variant]").forEach(function (b) { b.classList.remove("active"); });
      qvSizeBtn.classList.add("active");
    }

    /* Card click (anywhere except buttons/links already handled) navigates to product */
    var cardEl = e.target.closest("[data-pid]");
    if (cardEl && !e.target.closest("a, button")) {
      var link = cardEl.querySelector(".card-name");
      if (link) window.location.href = link.getAttribute("href");
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      closeDrawer();
      if (searchOverlay) searchOverlay.classList.remove("open");
      if (qvOverlay) qvOverlay.classList.remove("open");
    }
  });

  /* ---------- Scroll reveal ---------- */
  if ("IntersectionObserver" in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll(".reveal-el").forEach(function (el) { io.observe(el); });
  } else {
    document.querySelectorAll(".reveal-el").forEach(function (el) { el.classList.add("in"); });
  }

  /* ---------- Collection: mobile filter drawer + AJAX filtering ---------- */
  var filtersPanel = document.getElementById("filters");
  var mobileFilterToggle = document.getElementById("mobileFilterToggle");
  var filtersClose = document.getElementById("filtersClose");
  var filtersBackdrop = document.getElementById("filtersBackdrop");
  function openFilters() {
    if (!filtersPanel) return;
    filtersPanel.classList.add("open");
    if (filtersBackdrop) filtersBackdrop.classList.add("open");
    document.body.style.overflow = "hidden";
  }
  function closeFilters() {
    if (!filtersPanel) return;
    filtersPanel.classList.remove("open");
    if (filtersBackdrop) filtersBackdrop.classList.remove("open");
    document.body.style.overflow = "";
  }
  if (mobileFilterToggle) mobileFilterToggle.addEventListener("click", openFilters);
  if (filtersClose) filtersClose.addEventListener("click", closeFilters);
  if (filtersBackdrop) filtersBackdrop.addEventListener("click", closeFilters);

  var collectionSection = document.getElementById("collectionFiltersSection");
  if (collectionSection) {
    var filterForm = document.getElementById("filterForm");
    var sectionId = collectionSection.getAttribute("data-section-id");

    function refreshCollection(url) {
      collectionSection.style.opacity = "0.5";
      fetch(url.toString().indexOf("section_id=") === -1
        ? url + (url.indexOf("?") === -1 ? "?" : "&") + "section_id=" + sectionId
        : url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var wrapper = document.createElement("div");
          wrapper.innerHTML = html;
          var fresh = wrapper.querySelector("#collectionFiltersSection");
          if (fresh) {
            collectionSection.innerHTML = fresh.innerHTML;
            collectionSection.style.opacity = "";
            window.history.pushState({}, "", url.toString().replace(/([&?])section_id=[^&]+/, ""));
            window.scrollTo({ top: collectionSection.getBoundingClientRect().top + window.scrollY - 100, behavior: "smooth" });
          } else {
            window.location.href = url;
          }
        })
        .catch(function () { window.location.href = url; });
    }

    if (filterForm) {
      filterForm.addEventListener("change", function (e) {
        if (!e.target.matches("[data-auto-submit]")) return;
        var params = new URLSearchParams(new FormData(filterForm));
        var url = filterForm.getAttribute("action") + "?" + params.toString();
        refreshCollection(url);
      });
      filterForm.addEventListener("submit", function (e) {
        e.preventDefault();
        var params = new URLSearchParams(new FormData(filterForm));
        var url = filterForm.getAttribute("action") + "?" + params.toString();
        refreshCollection(url);
        closeFilters();
      });
    }
  }
})();
